import type { TWidget } from 'widgets/Widget';
import type { TAjaxForm } from 'widgets/forms/AjaxForm';

import { setCookie, getCookie } from 'widgets/toolbox/cookie';
import { TModalInstance } from 'widgets/global/Modal';
import { debounce } from 'widgets/toolbox/debounce';
import { getViewType } from 'widgets/toolbox/viewtype';

export type TCoreSubscriptionsFormResponse = TServerResponse & {
    customerData?: {
        isSubscriber?: boolean;
    };
};

/**
 * @description NewsletterFlyIn implementation
 * @param Widget Base widget for extending
 * @returns NewsletterFlyIn class
 */
function NewsletterFlyInClassCreator(Widget: TWidget) {
    class NewsletterFlyIn extends Widget {
        private scrollListener?: () => void;

        private exitIntentListener?: (event: MouseEvent) => void;

        private mobileExitIntentListener?: () => void;

        private hasTriggered: boolean = false;

        private lastScrollTop: number = 0;

        private readonly scrollVelocityThreshold: number = 50;

        prefs() {
            const viewType = getViewType();

            const isMobile = ['small', 'medium'].includes(viewType);

            return {
                flyInButtonRef: 'flyInButton',
                flyInContentRef: 'flyInContent',
                buttonHiddenClass: 'm-hidden',
                buttonActiveClass: 'm-active',
                newsletterSubmittedClass: 'm-submitted',
                is1774Page: false,
                scrollDepthTrigger: 35, // 35% scroll depth for 1774 pages
                isMobile,
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization
         */
        init() {
            const prefs = this.prefs();
            const cookieName = this.getCookieName();

            if (!getCookie(cookieName)) {
                this.ref('self').get()?.classList.add(prefs.buttonActiveClass);

                if (prefs.is1774Page && window.contexts.includes('home')) {
                    this.setupScrollTrigger();
                } else if (prefs.is1774Page && (window.contexts.includes('plp') || window.contexts.includes('clp'))) {
                    this.setupExitIntentTriggers();
                }
            }

            this.eventBus().on('newsletter.subscribed', 'changeNewsletterView');
        }

        /**
         * @description Set up scroll depth trigger for 1774 pages
         */
        setupScrollTrigger() {
            if (this.hasTriggered) {
                return;
            }

            this.scrollListener = debounce(() => {
                this.checkScrollDepth();
            }, 100);

            window.addEventListener('scroll', this.scrollListener, { passive: true });
        }

        /**
         * @description Check if scroll depth threshold is reached
         */
        checkScrollDepth() {
            if (this.hasTriggered) {
                return;
            }

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const documentHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const scrollPercentage = (scrollTop / documentHeight) * 100;

            if (scrollPercentage >= this.prefs().scrollDepthTrigger) {
                this.triggerFlyIn();
            }
        }

        /**
         * @description Trigger the fly-in to show
         */
        triggerFlyIn() {
            this.hasTriggered = true;

            const cookieName = this.getCookieName();

            setCookie(cookieName, '1');

            this.showModal();
            this.cleanupListeners();
        }

        /**
         * @description Handles click event to hide flyIn (for 1774 pages)
         */
        flyInClose() {
            this.cancel();
            this.hideModal();
        }

        /**
         * @description Handles click event to hide flyIn button
         */
        cancel() {
            const prefs = this.prefs();
            const elementButton = this.ref(prefs.flyInButtonRef).get();

            elementButton?.classList.add(prefs.buttonHiddenClass);

            this.cleanupListeners();
        }

        /**
         * @description Handles click event on the flyIn button to show the modal
         */
        showModal() {
            this.getById('newsletterFlyinPopup', (newsletterFlyinPopup: TModalInstance) => {
                return newsletterFlyinPopup.showModal();
            });
        }

        /**
         * @description Handles click event on the flyIn button to hide the modal
         */
        hideModal() {
            this.getById('newsletterFlyinPopup', (newsletterFlyinPopup: TModalInstance) => {
                return newsletterFlyinPopup.cancel();
            });
        }

        /**
         * @description Show message after newsletter form submission
         */
        changeNewsletterView(form: TAjaxForm, data: TCoreSubscriptionsFormResponse) {
            this.getById('newsletterFlyinPopup', (newsletterFlyinPopup: TModalInstance) => {
                if (data?.success) {
                    newsletterFlyinPopup.ref('innerContainer').hide();

                    if (data?.customerData?.isSubscriber) {
                        newsletterFlyinPopup.ref('alreadySubscribedContainer').show();
                    } else {
                        newsletterFlyinPopup.ref('successContainer').show();
                    }
                }
            });
        }

        /**
         * @description Clean up event listeners when widget is destroyed
         */
        destroy() {
            this.cleanupListeners();
            super.destroy();
        }

        /**
         * @description Get the appropriate cookie name based on page type
         */
        getCookieName(): string {
            return this.prefs().is1774Page ? 'flyin_1774_closed' : 'flyin_closed';
        }

        /**
         * @description Set up exit intent triggers for PLP/CLP pages
         */
        setupExitIntentTriggers() {
            if (this.hasTriggered) {
                return;
            }

            const prefs = this.prefs();

            this.exitIntentListener = (event: MouseEvent) => {
                this.handleExitIntent(event);
            };

            document.addEventListener('mouseleave', this.exitIntentListener);
            document.addEventListener('mouseout', this.exitIntentListener);

            if (prefs.isMobile) {
                this.mobileExitIntentListener = () => {
                    this.handleMobileExitIntent();
                };

                window.addEventListener('scroll', this.mobileExitIntentListener, { passive: true });
            }
        }

        /**
         * @description Handle desktop exit intent (mouse moving to top of browser navigation bar)
         */
        handleExitIntent(event: MouseEvent) {
            if (this.hasTriggered) {
                return;
            }

            if (event.clientY <= 0) {
                this.triggerFlyIn();
            }
        }

        /**
         * @description Handle mobile exit intent (fast upward scroll)
         */
        handleMobileExitIntent() {
            if (this.hasTriggered) {
                return;
            }

            const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollDelta = this.lastScrollTop - currentScrollTop;

            if (scrollDelta > this.scrollVelocityThreshold && currentScrollTop >= 0) {
                this.triggerFlyIn();
            }

            this.lastScrollTop = currentScrollTop;
        }

        /**
         * @description Clean up all event listeners
         */
        cleanupListeners() {
            if (this.scrollListener) {
                window.removeEventListener('scroll', this.scrollListener);
                this.scrollListener = undefined;
            }

            if (this.exitIntentListener) {
                document.removeEventListener('mouseleave', this.exitIntentListener);
                document.removeEventListener('mouseout', this.exitIntentListener);
                this.exitIntentListener = undefined;
            }

            if (this.mobileExitIntentListener) {
                window.removeEventListener('scroll', this.mobileExitIntentListener);
                this.mobileExitIntentListener = undefined;
            }
        }
    }

    return NewsletterFlyIn;
}

export type TNewsletterFlyIn = ReturnType<typeof NewsletterFlyInClassCreator>;

export type TNewsletterFlyInInstance = InstanceType<TNewsletterFlyIn>;

export default NewsletterFlyInClassCreator;
